import React, { useEffect, useState } from 'react';
import {
  collection, query, where, getDocs,
  addDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import LoadingSpinner from '../components/LoadingSpinner';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';

const sortIssuesByDate = (issues) =>
  [...issues].sort((a, b) => (b.reportedAt?.toMillis?.() || 0) - (a.reportedAt?.toMillis?.() || 0));

function ReportIssues({ user }) {
  const [catalogue, setCatalogue] = useState([]);
  const [loadingCatalogue, setLoadingCatalogue] = useState(true);
  const [selectedItem, setSelectedItem] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [error, setError] = useState('');
  const [myIssues, setMyIssues] = useState([]);
  const [loadingIssues, setLoadingIssues] = useState(true);

  useEffect(() => {
    const fetchCatalogue = async () => {
      try {
        const q = query(
          collection(db, 'catalogue'),
          where('status', '==', 'catalogue')
        );
        const snap = await getDocs(q);
        setCatalogue(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => (a.title || '').localeCompare(b.title || '')));
      } catch (err) {
        console.error('Failed to fetch catalogue:', err);
      } finally {
        setLoadingCatalogue(false);
      }
    };

    const fetchIssues = async () => {
      try {
        const q = query(
          collection(db, 'issues'),
          where('reportedBy', '==', user.email)
        );
        const snap = await getDocs(q);
        setMyIssues(sortIssuesByDate(snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
      } catch (err) {
        console.error('Failed to fetch issues:', err);
      } finally {
        setLoadingIssues(false);
      }
    };

    fetchCatalogue();
    fetchIssues();
  }, [user.email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedItem || !description.trim()) return;
    const item = catalogue.find(c => c.id === selectedItem);
    if (!item) return;

    setSubmitting(true);
    setError('');
    setSuccessMsg('');

    try {
      await addDoc(collection(db, 'issues'), {
        catalogueItemId: item.id,
        title: item.title,
        description: description.trim(),
        reportedBy: user.email,
        reportedAt: serverTimestamp(),
        status: 'open',
      });
      setSuccessMsg(`Issue reported for "${item.title}". Thank you!`);
      setSelectedItem('');
      setDescription('');

      const q = query(
        collection(db, 'issues'),
        where('reportedBy', '==', user.email)
      );
      const snap = await getDocs(q);
      setMyIssues(sortIssuesByDate(snap.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
    } catch (err) {
      setError('Failed to submit issue. Please try again.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const issueStatusColor = (status) => {
    if (status === 'resolved') return 'success';
    if (status === 'in_progress') return 'info';
    return 'warning';
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>Report an Issue</Typography>

      {/* Report Form */}
      <Paper sx={{ p: 3, mb: 4.5, borderRadius: 2 }}>
        {loadingCatalogue ? (
          <LoadingSpinner message="Loading catalogue…" />
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <FormControl fullWidth size="small" required>
              <InputLabel>Select Title</InputLabel>
              {catalogue.length === 0 ? (
                <Typography color="text.secondary" sx={{ mt: 1 }}>No titles in catalogue yet.</Typography>
              ) : (
                <Select
                  value={selectedItem}
                  label="Select Title"
                  onChange={e => setSelectedItem(e.target.value)}
                >
                  <MenuItem value=""><em>Choose a movie or TV show…</em></MenuItem>
                  {catalogue.map(item => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.title} ({item.type === 'tv' ? 'TV' : 'Movie'})
                    </MenuItem>
                  ))}
                </Select>
              )}
            </FormControl>

            <TextField
              label="Describe the Issue"
              multiline
              rows={4}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="e.g. Subtitles are out of sync, video stutters at 45 minutes…"
              required
              fullWidth
              size="small"
            />

            {successMsg && <Alert severity="success">{successMsg}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}

            <Button
              type="submit"
              variant="contained"
              disabled={submitting || !selectedItem || !description.trim()}
              sx={{ alignSelf: 'flex-start' }}
            >
              {submitting ? 'Submitting…' : 'Submit Report'}
            </Button>
          </Box>
        )}
      </Paper>

      {/* My Issues */}
      <Typography variant="h6" sx={{ mb: 2 }}>My Reports</Typography>
      {loadingIssues ? (
        <LoadingSpinner message="Loading your reports…" />
      ) : myIssues.length === 0 ? (
        <Typography color="text.secondary">You haven't reported any issues yet.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {myIssues.map(issue => (
            <Paper key={issue.id} sx={{ p: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" fontWeight={600}>{issue.title}</Typography>
                <Chip
                  label={issue.status || 'open'}
                  size="small"
                  color={issueStatusColor(issue.status)}
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
              </Box>
              <Typography variant="body2" color="text.primary" sx={{ mb: 0.75 }}>{issue.description}</Typography>
              {issue.reportedAt?.toDate && (
                <Typography variant="caption" color="text.secondary">
                  Reported {issue.reportedAt.toDate().toLocaleDateString()}
                </Typography>
              )}
            </Paper>
          ))}
        </Box>
      )}
    </Container>
  );
}

export default ReportIssues;
